// src/utils/workoutUtils.js
export const processExercises = (rawExercises, formData) => {
  const workoutType = formData.workout.toLowerCase();
  const workoutIntensity = formData.intensity.toLowerCase();

  let processedExercises = [];
  let sharedReplacements = [];

  switch (workoutType) {
    case "cardio":
      if (workoutIntensity === "intense") {
        const hiitWorkout = processHIIT(rawExercises);
        processedExercises = [
          ...(hiitWorkout.warmup ? [hiitWorkout.warmup] : []),
          ...hiitWorkout.hiitExercises.map((ex) => ({
            ...ex,
            replacement: hiitWorkout.sharedReplacements,
          })),
          ...(hiitWorkout.stretching ? [hiitWorkout.stretching] : []),
          ...(hiitWorkout.optionalCardio ? [hiitWorkout.optionalCardio] : []),
        ];
        sharedReplacements = [...hiitWorkout.sharedReplacements, ...(hiitWorkout.stretchingReplacements || []), ...(hiitWorkout.optionalCardioReplacements || [])];
      } else {
        const cardio = processCardio(rawExercises);
        processedExercises = cardio.exercises;
        sharedReplacements = cardio.sharedReplacements;
      }
      break;

    case "stretch":
      const stretch = processStretch(rawExercises);
      processedExercises = [
        ...(stretch.warmup ? [stretch.warmup] : []),
        ...stretch.stretchingExercises.map((ex) => ({
          ...ex,
          replacement: stretch.stretchingReplacements,
        })),
      ];
      sharedReplacements = stretch.stretchingReplacements;
      break;

    default:
      processedExercises = processDefault(rawExercises);
      break;
  }

  return { processedExercises, sharedReplacements };
};

export const processCardio = (rawExercises) => {
  const mainCount = Math.floor(rawExercises.length / 4);
  const mainExercises = rawExercises.slice(0, mainCount);
  const replacements = rawExercises.slice(mainCount);
  const cardioExercises = mainExercises.map((exercise) => ({
    ...exercise,
    replacement: replacements,
  }));
  return { exercises: cardioExercises, sharedReplacements: replacements };
};

export const processHIIT = (rawExercises) => {
  const workout = {
    warmup: null,
    hiitExercises: [],
    stretching: null,
    optionalCardio: null,
    sharedReplacements: [],
    warmupReplacements: [],
    stretchingReplacements: [],
    optionalCardioReplacements: [],
  };

  const isValidExercise = (exercise) => exercise && exercise.exercise_type;
  let i = 0;

  if (i < rawExercises.length && isValidExercise(rawExercises[i]) && rawExercises[i].exercise_type.toLowerCase() === "cardio") {
    const warmupGroup = rawExercises.slice(i, i + 4).filter(isValidExercise);
    if (warmupGroup.length > 0) {
      workout.warmup = {
        ...warmupGroup[0],
        replacement: warmupGroup.slice(1),
      };
      workout.warmupReplacements = warmupGroup.slice(1);
      i += 4;
    }
  }

  let hiitExercises = [];
  let nullCount = 0;
  while (i < rawExercises.length && (isValidExercise(rawExercises[i]) || rawExercises[i] === null || (rawExercises[i] && rawExercises[i].exercise_type.toLowerCase() === "hiit"))) {
    if (isValidExercise(rawExercises[i]) && rawExercises[i].exercise_type.toLowerCase() === "hiit") {
      hiitExercises.push(rawExercises[i]);
    } else if (rawExercises[i] === null) {
      nullCount++;
    } else {
      break;
    }
    i += 1;
  }
  const totalHIITIncludingNulls = hiitExercises.length + nullCount;
  const mainHIITCount = Math.ceil(totalHIITIncludingNulls / 4);
  workout.hiitExercises = hiitExercises.slice(0, mainHIITCount);
  workout.sharedReplacements = hiitExercises.slice(mainHIITCount);

  for (let j = i; j < rawExercises.length; j++) {
    const exercise = rawExercises[j];
    if (isValidExercise(exercise) && exercise.exercise_type.toLowerCase() === "cardio") {
      const cardioGroup = rawExercises.slice(j, j + 4).filter(isValidExercise);
      if (cardioGroup.length > 0) {
        workout.optionalCardio = {
          ...cardioGroup[0],
          replacement: cardioGroup.slice(1),
        };
        workout.optionalCardioReplacements = cardioGroup.slice(1);
        i = j + 4;
      }
      break;
    }
  }

  for (let j = i; j < rawExercises.length; j++) {
    const exercise = rawExercises[j];
    if (isValidExercise(exercise) && exercise.exercise_type.toLowerCase() === "stretch") {
      const stretchGroup = rawExercises.slice(j, j + 4).filter(isValidExercise);
      if (stretchGroup.length > 0) {
        workout.stretching = {
          ...stretchGroup[0],
          replacement: stretchGroup.slice(1),
        };
        workout.stretchingReplacements = stretchGroup.slice(1);
        i = j + 4;
      }
      break;
    }
  }

  return workout;
};

export const processStretch = (rawExercises) => {
  const workout = {
    warmup: null,
    stretchingExercises: [],
    stretchingReplacements: [],
    warmupReplacements: [],
  };

  let i = 0;
  if (rawExercises[0] && rawExercises[0].exercise_type.toLowerCase() === "cardio") {
    const warmupGroup = rawExercises.slice(i, i + 4).filter(Boolean);
    if (warmupGroup.length > 0) {
      workout.warmup = {
        ...warmupGroup[0],
        replacement: warmupGroup.slice(1),
      };
      workout.warmupReplacements = warmupGroup.slice(1);
      i += 4;
    }
  }
  while (i < rawExercises.length) {
    const group = rawExercises.slice(i, i + 4).filter(Boolean);
    if (group.length > 0) {
      const mainExercise = group[0];
      workout.stretchingExercises.push(mainExercise);
      workout.stretchingReplacements.push(...group.slice(1));
      i += 4;
    }
  }
  return workout;
};

export const processDefault = (rawExercises) => {
  const groupedExercises = [];
  let j = 0;
  while (j < rawExercises.length) {
    const group = rawExercises.slice(j, j + 4);
    if (group.length === 4) {
      const mainExercise = { ...group[0], replacement: group.slice(1) };
      groupedExercises.push(mainExercise);
      j += 4;
    } else {
      group.forEach((exercise) => {
        groupedExercises.push({ ...exercise, replacement: [] });
      });
      break;
    }
  }
  return groupedExercises;
};

export const convertToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  if (typeof timeStr !== "string") return 0;
  const lowerCaseStr = timeStr.toLowerCase().trim();
  if (lowerCaseStr === "n/a") return 0;
  if (lowerCaseStr.includes("min")) {
    const timeParts = lowerCaseStr.split(":");
    let minutes = 0;
    let seconds = 0;
    if (timeParts.length === 2) {
      minutes = parseInt(timeParts[0], 10) || 0;
      seconds = parseInt(timeParts[1].replace(/\D/g, ""), 10) || 0;
    } else {
      minutes = parseInt(lowerCaseStr.replace(/\D/g, ""), 10) || 0;
    }
    return minutes * 60 + seconds;
  } else if (lowerCaseStr.includes("s")) {
    const seconds = parseInt(lowerCaseStr.replace(/\D/g, ""), 10);
    return isNaN(seconds) ? 0 : seconds;
  }
  const num = parseInt(lowerCaseStr.replace(/\D/g, ""), 10);
  return isNaN(num) ? 0 : num;
};

export const parseReps = (value) => {
  if (typeof value === "number") {
    return `C${value}`;
  }
  if (typeof value === "string") {
    const lowerCaseStr = value.toLowerCase().trim();
    if (lowerCaseStr.includes("min") || lowerCaseStr.includes("s")) {
      const repsInSeconds = convertToSeconds(value);
      return `T${repsInSeconds}`;
    } else if (lowerCaseStr.includes("-")) {
      const range = lowerCaseStr.match(/(\d+)-(\d+)/);
      if (range) {
        const [_, minReps, maxReps] = range;
        return `C${minReps}-${maxReps}`;
      }
      const parsed = parseInt(value, 10);
      return `C${isNaN(parsed) ? 0 : parsed}`;
    } else {
      const parsed = parseInt(value, 10);
      return `C${isNaN(parsed) ? 0 : parsed}`;
    }
  }
  return `C0`;
};

export const formatFitnessGoal = (fitnessGoal) => {
  const mapping = {
    musclegain: "Muscle Gain",
    strength: "Strength",
    weightloss: "Weight Loss",
    generalhealth: "General Health",
  };
  return fitnessGoal ? mapping[fitnessGoal.toLowerCase()] || fitnessGoal : "";
};

export const formatExperience = (experience) => {
  if (!experience) return "";
  return experience.charAt(0).toUpperCase() + experience.slice(1).toLowerCase();
};

export const formatWorkout = (workout) => {
  if (!workout) return "";
  return workout.toLowerCase() === "fullbody" ? "Full Body" : workout.charAt(0).toUpperCase() + workout.slice(1).toLowerCase();
};

export const formatTime = (time) => `${time} min`;

export const formatIntensity = (intensity) => {
  if (!intensity) return "";
  return intensity.charAt(0).toUpperCase() + intensity.slice(1).toLowerCase();
};
